import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Play,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type DataQualityReport, type CheckResult } from '@/types';
import { periodsApi, dataQualityApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

const gradeColors: Record<string, { bg: string; text: string; ring: string }> = {
  A: { bg: 'bg-green-500', text: 'text-green-700', ring: '#22c55e' },
  B: { bg: 'bg-blue-500', text: 'text-blue-700', ring: '#3b82f6' },
  C: { bg: 'bg-amber-500', text: 'text-amber-700', ring: '#f59e0b' },
  D: { bg: 'bg-orange-500', text: 'text-orange-700', ring: '#f97316' },
  F: { bg: 'bg-red-500', text: 'text-red-700', ring: '#ef4444' },
};

const severityConfig: Record<string, { label: string; bg: string; text: string }> = {
  critical: { label: 'Critical', bg: 'bg-red-50', text: 'text-red-700' },
  high: { label: 'High', bg: 'bg-orange-50', text: 'text-orange-700' },
  medium: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700' },
  low: { label: 'Low', bg: 'bg-blue-50', text: 'text-blue-700' },
};

const categoryTabs = ['Completeness', 'Accuracy', 'Consistency', 'Timeliness'];

function getGradeColor(grade: string): string {
  return gradeColors[grade]?.ring || '#9ca3af';
}

// --- Main Page ---------------------------------------------------------------

export default function DataQuality() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [reports, setReports] = useState<DataQualityReport[]>([]);
  const [activeTab, setActiveTab] = useState('Completeness');

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load quality data
  const loadData = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await dataQualityApi.summary(Number(selectedPeriod));
      setReports(Array.isArray(res) ? res : []);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load data quality reports');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadData();
  }, [loadData, selectedPeriod]);

  // Run quality checks
  const handleRun = async () => {
    if (!selectedPeriod) return;
    setRunning(true);
    try {
      await dataQualityApi.run(Number(selectedPeriod));
      await loadData();
      addNotification({ type: 'success', title: 'Quality Checks Complete', message: 'Data quality checks have finished.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Check Failed', message: apiErr.detail || 'Data quality checks failed.' });
    } finally {
      setRunning(false);
    }
  };

  // Aggregated stats
  const totalChecks = reports.reduce((s, r) => s + r.checks_total, 0);
  const totalPassed = reports.reduce((s, r) => s + r.checks_passed, 0);
  const totalFailed = reports.reduce((s, r) => s + r.checks_failed, 0);
  const totalCritical = reports.reduce((s, r) => s + r.critical_failures.length, 0);

  // Average score
  const avgScore = reports.length > 0
    ? Math.round(reports.reduce((s, r) => s + r.score, 0) / reports.length)
    : 0;
  const avgGrade = avgScore >= 90 ? 'A' : avgScore >= 80 ? 'B' : avgScore >= 70 ? 'C' : avgScore >= 60 ? 'D' : 'F';

  // Donut chart data
  const donutData = [
    { name: 'Passed', value: totalPassed, color: '#22c55e' },
    { name: 'Failed', value: totalFailed, color: '#ef4444' },
  ];

  // Entity scores bar chart (sorted by score ascending)
  const entityBarData = [...reports]
    .sort((a, b) => a.score - b.score)
    .map((r) => ({
      entity: r.entity_name.length > 20 ? r.entity_name.slice(0, 18) + '...' : r.entity_name,
      score: r.score,
      grade: r.grade,
    }));

  // All check results across reports, filtered by active tab category
  const allResults: (CheckResult & { entity_name: string })[] = reports.flatMap((r) =>
    r.results.map((c) => ({ ...c, entity_name: r.entity_name })),
  );

  const filteredResults = allResults.filter(
    (c) => c.category.toLowerCase() === activeTab.toLowerCase(),
  );

  // Skeleton loading
  if (isLoading && reports.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <div className="skeleton w-56 h-7 mb-1" />
            <div className="skeleton w-80 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-16 h-3 mb-3" />
              <div className="skeleton w-12 h-8" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6 animate-pulse"><div className="skeleton w-full h-64" /></div>
          <div className="card p-6 animate-pulse"><div className="skeleton w-full h-64" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Data Quality Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Monitor and improve data quality across entities</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleRun}
            disabled={running || !selectedPeriod}
            className="btn-primary"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Quality Checks
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data */}
      {reports.length === 0 && !error ? (
        <div className="card py-16 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No quality data available</h3>
          <p className="text-sm text-gray-500 mt-1">
            Select a period and click "Run Quality Checks" to analyze data quality.
          </p>
        </div>
      ) : reports.length > 0 && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Checks</p>
              <p className="text-3xl font-bold text-gray-900 mt-1 font-mono">{totalChecks}</p>
            </div>
            <div className="card p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Passed</p>
              <p className="text-3xl font-bold text-green-700 mt-1 font-mono">{totalPassed}</p>
            </div>
            <div className="card p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Failed</p>
              <p className="text-3xl font-bold text-red-700 mt-1 font-mono">{totalFailed}</p>
            </div>
            <div className="card p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Critical Failures</p>
              <p className="text-3xl font-bold text-red-700 mt-1 font-mono">{totalCritical}</p>
              {totalCritical > 0 && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Requires attention
                </p>
              )}
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quality Score Donut */}
            <div className="card p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Overall Quality Score</h2>
              <div className="flex items-center gap-8">
                <div className="relative flex-shrink-0" style={{ width: 180, height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        outerRadius={85}
                        innerRadius={60}
                        dataKey="value"
                        paddingAngle={2}
                        startAngle={90}
                        endAngle={-270}
                      >
                        {donutData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold" style={{ color: getGradeColor(avgGrade) }}>
                      {avgScore}%
                    </span>
                    <span className="text-sm font-semibold text-gray-500">Grade {avgGrade}</span>
                  </div>
                </div>
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm text-gray-700">Passed: <span className="font-mono font-bold text-green-700">{totalPassed}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm text-gray-700">Failed: <span className="font-mono font-bold text-red-700">{totalFailed}</span></span>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      {reports.length} entities analyzed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Entity Scores Bar Chart */}
            <div className="card p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Entity Quality Scores</h2>
              {entityBarData.length === 0 ? (
                <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
                  No entity data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, entityBarData.length * 36)}>
                  <BarChart data={entityBarData} layout="vertical" barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="entity"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#374151' }}
                      width={140}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      formatter={(value: number) => [`${value}%`, 'Score']}
                    />
                    <Bar dataKey="score" name="Score" radius={[0, 4, 4, 0]}>
                      {entityBarData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getGradeColor(entry.grade)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Category Tabs & Check Results */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-1">
                {categoryTabs.map((tab) => {
                  const count = allResults.filter(
                    (c) => c.category.toLowerCase() === tab.toLowerCase(),
                  ).length;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={clsx(
                        'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                        activeTab === tab
                          ? 'bg-primary-700 text-white'
                          : 'text-gray-600 hover:bg-gray-200',
                      )}
                    >
                      {tab}
                      <span className={clsx(
                        'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                        activeTab === tab
                          ? 'bg-white/20 text-white'
                          : 'bg-gray-200 text-gray-600',
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-6">
              {filteredResults.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-green-300 mb-2" />
                  <p className="text-sm text-gray-500">No checks in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredResults.map((check, idx) => (
                    <div
                      key={`${check.check_id}-${idx}`}
                      className={clsx(
                        'rounded-lg border p-4',
                        check.passed
                          ? 'border-green-200 bg-green-50/50'
                          : 'border-red-200 bg-red-50/50',
                      )}
                    >
                      <div className="flex items-start gap-3">
                        {check.passed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900">{check.description}</p>
                            {!check.passed && (
                              <span className={clsx(
                                'badge text-xs',
                                severityConfig[check.severity]?.bg || 'bg-gray-100',
                                severityConfig[check.severity]?.text || 'text-gray-700',
                              )}>
                                {severityConfig[check.severity]?.label || check.severity}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mb-1">{check.entity_name}</p>
                          {!check.passed && (
                            <>
                              <p className="text-xs text-red-600 mt-1">{check.details}</p>
                              {check.suggestion && (
                                <div className="mt-2 p-2 rounded bg-white border border-gray-200">
                                  <p className="text-xs text-gray-600">
                                    <span className="font-semibold">Suggestion:</span> {check.suggestion}
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
